/**
 * Pure rules behind "ring colleagues into this call" (#1384).
 *
 * They live in lib/ rather than inside the picker because the call module has
 * no component-test harness — lib/ and hooks/ are the only surfaces
 * `test-frontend-shell.yml` can actually execute. The two rules worth pinning
 * are the one the server also enforces (the cap, so the picker never composes
 * a request that is already known to fail) and the one that is easiest to
 * silently regress (who is offered the control at all).
 */

/**
 * Colleagues per ring action.
 *
 * Mirrors `DirectCallRingIntoRoomSerializer.MAX_CALLEES_PER_ACTION` on
 * services/video. The server is the authority; this constant only keeps the UI
 * from offering an eighth pick and then failing the request.
 *
 * Per *action*, not per room (D18): ring seven, reopen the picker, ring seven
 * more. No cumulative room ceiling exists anywhere in the service, and that is
 * deliberate — recorded here so it doesn't read as an oversight later.
 */
export const MAX_RING_PER_ACTION = 7;

export function isRingSelectionFull(selectedSubs: readonly string[]): boolean {
  return selectedSubs.length >= MAX_RING_PER_ACTION;
}

/**
 * Add or remove one colleague, keyed on `sub`.
 *
 * `sub` and not name: `User.name` has no unique constraint, so two colleagues
 * can genuinely share a display name — and `sub` is exactly what the ring
 * endpoint takes, so no mapping happens between picking and sending.
 *
 * Deselecting is always allowed, including at the cap; only *adding* is capped.
 * Re-selecting someone already picked is a removal, not a duplicate — which is
 * also why the server dedupes before counting.
 */
export function toggleRingSelection(
  selectedSubs: readonly string[],
  sub: string,
): string[] {
  if (selectedSubs.includes(sub)) {
    return selectedSubs.filter((s) => s !== sub);
  }
  if (isRingSelectionFull(selectedSubs)) {
    return [...selectedSubs];
  }
  return [...selectedSubs, sub];
}

export interface RingControlContext {
  /**
   * True when the viewer joined through a guest link.
   *
   * Guests never reach this code in practice — `GuestRoomRoute` renders the
   * legacy `MeetingRoom`, while `InCallView` (which owns the control) is the
   * authenticated route. The flag exists so the rule is *stated* rather than
   * left to routing alone, and so it can be tested.
   */
  isGuestSession: boolean;
}

/**
 * Whether the ring-colleagues control is offered.
 *
 * Note what is **absent**: there is no owner/host input. This control is
 * deliberately participant-visible (D16/D21, DoD B) — unlike every other
 * in-call invite affordance in this module, which is keyed off
 * `room.is_administrable`. Anyone already in the call may pull a colleague in;
 * the server enforces the same rule (an access row *or* an accepted invite,
 * never ownership). Adding an owner condition here would break AC1.
 */
export function canRingColleaguesIntoCall(ctx: RingControlContext): boolean {
  return !ctx.isGuestSession;
}
